import React, { useMemo } from 'react';
import { porDiaSemana } from '../../utils/agregaciones';
import Money from './Money';

/**
 * Cuánto deja cada día de la semana.
 *
 * Va en HTML y no en Chart.js a propósito: son siete filas fijas, entran los
 * montos escritos al lado de cada barra y así se lee sin pasar el mouse por
 * encima. Un gráfico con tooltip escondería el dato que justamente se quiere
 * comparar de un vistazo.
 *
 * La barra mide el PROMEDIO de un día (suma del día entero, promediada entre
 * fechas), no el promedio por cierre: los restaurantes que cierran dos veces
 * por día —almuerzo y cena— si no mostrarían medio día en cada barra.
 */
export default function BarrasDiaSemana({ history = [] }) {
  const datos = useMemo(() => porDiaSemana(history), [history]);

  const max = Math.max(...datos.map((d) => d.promedio), 0);
  const diasConDatos = datos.reduce((s, d) => s + d.dias, 0);

  if (diasConDatos === 0) {
    return (
      <div className="si-caption py-8 text-center">
        No hay cierres en el período elegido.
      </div>
    );
  }

  // Con menos de dos semanas cada día tiene una o dos muestras: el promedio
  // existe pero no dice gran cosa, y conviene avisarlo antes que callarlo.
  const muestraChica = diasConDatos < 14;

  return (
    <>
      <div className="space-y-2.5">
        {datos.map((d) => {
          const ancho = max > 0 ? Math.max(2, (d.promedio / max) * 100) : 0;
          const esFinde = d.diaSemana === 0 || d.diaSemana === 6;
          return (
            <div key={d.etiqueta} className="flex items-center gap-3">
              <div className="w-9 text-xs font-semibold text-ink-2 flex-shrink-0">
                {d.etiqueta}
              </div>
              <div className="flex-1 min-w-0">
                <div className="h-6 rounded-md bg-surface-2 overflow-hidden relative">
                  <div
                    className="h-full rounded-md transition-all duration-300"
                    style={{
                      width: `${ancho}%`,
                      // El fin de semana se distingue porque es una propiedad
                      // del día, no su posición en el ranking.
                      background: esFinde ? '#b28cf5' : 'rgba(178, 140, 245, 0.45)',
                    }}
                  />
                </div>
              </div>
              <div className="flex items-baseline gap-2 whitespace-nowrap w-40 justify-end">
                <span className="text-xs text-ink-3 si-money">
                  {d.porcentaje.toFixed(1).replace('.', ',')}%
                </span>
                <span className="text-sm font-semibold text-ink">
                  <Money value={d.promedio} />
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="si-caption mt-4">
        Promedio de lo que deja un día completo, sobre {diasConDatos} día
        {diasConDatos === 1 ? '' : 's'} con cierres.
        {muestraChica && ' Con tan pocos días el promedio todavía se mueve mucho: mirá un rango más largo.'}
      </p>
    </>
  );
}
